import { z } from 'zod/v3';

// OpenAPI configuration stored in Topic.apiConfig
export const OpenApiConfigSchema = z.object({
  sourceUrl: z.string().url().nullish(),
  sourceFile: z.string().nullish(),
  lastImported: z.string().datetime(),
  enabled: z.boolean().default(true),
  autoSync: z.boolean().default(false),
  specTitle: z.string().nullish(),
  specVersion: z.string().nullish(),
});

export type OpenApiConfig = z.infer<typeof OpenApiConfigSchema>;

// Changelog generation strategy
export const ChangelogStrategySchema = z.enum([
  'release', // Generate by release/version tags
  'monthly', // Generate by month
  'quarterly', // Generate by quarter
  'yearly', // Generate by year
]);

export type ChangelogStrategy = z.infer<typeof ChangelogStrategySchema>;

// Changelog configuration stored in Topic.changelogConfig
export const ChangelogConfigSchema = z.object({
  gitUrl: z.string().url(),
  branch: z.string().default("main"),
  lastFetched: z.string().datetime().nullish(),
  templateId: z.string().nullish(),
  enabled: z.boolean().default(true),
  strategy: ChangelogStrategySchema.default('release'), // How to group commits
  commitRange: z
    .object({
      from: z.string(), // tag or commit hash
      to: z.string(), // tag or commit hash or HEAD
    })
    .nullish(),
  fromTag: z.string().nullish(), // For individual changelogs (release strategy)
  toTag: z.string().nullish(), // For individual changelogs (release strategy)
  month: z.string().nullish(), // For monthly changelogs (YYYY-MM format)
  year: z.number().nullish(), // For yearly/quarterly changelogs
  quarter: z.number().min(1).max(4).nullish(), // For quarterly changelogs
  generatedAt: z.string().datetime().nullish(),
});

export type ChangelogConfig = z.infer<typeof ChangelogConfigSchema>;

// ApiImport model DTO
export const ApiImportDtoSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  sourceUrl: z.string().url().nullish(),
  sourceFile: z.string().nullish(),
  importedAt: z.date(),
  importedBy: z.string(),
  status: z.enum(["success", "failed", "pending"]),
  specContent: z.any().nullish(), // OpenAPI spec as JSON
  error: z.string().nullish(),
});

export type ApiImportDto = z.infer<typeof ApiImportDtoSchema>;

// Import request DTOs
export const ImportOpenApiRequestSchema = z.object({
  workspaceId: z.string(),
  source: z.union([
    z.string().url(), // URL to OpenAPI spec
    z.object({
      // File upload
      name: z.string(),
      content: z.string(), // Base64 encoded or raw content
    }),
  ]),
  title: z.string().optional(), // Custom title for the API section
  customSlug: z.string().optional(), // Custom slug for the API section
  parentTopicId: z.string().optional(), // Parent topic ID
});

export type ImportOpenApiRequest = z.infer<typeof ImportOpenApiRequestSchema>;

export const ReimportOpenApiRequestSchema = z.object({
  topicId: z.string(),
});

export type ReimportOpenApiRequest = z.infer<typeof ReimportOpenApiRequestSchema>;

// Changelog creation request
export const CreateChangelogRequestSchema = z.object({
  workspaceId: z.string(),
  parentId: z.string().optional(), // Parent topic ID for organizing changelog entries
  title: z.string(),
  gitUrl: z.string().url(),
  branch: z.string().default("main"),
  strategy: ChangelogStrategySchema.default('release'),
  // For release strategy
  fromTag: z.string().optional(),
  toTag: z.string().optional(),
  // For time-based strategies
  month: z.string().optional(), // YYYY-MM format
  year: z.number().optional(),
  quarter: z.number().min(1).max(4).optional(),
  // Template
  templateId: z.string().optional(),
}).refine(
  (data) => {
    // Validate based on strategy
    if (data.strategy === 'release') {
      // fromTag is optional (for getting all history), but toTag is required
      return !!data.toTag;
    } else if (data.strategy === 'monthly') {
      return data.month && /^\d{4}-\d{2}$/.test(data.month);
    } else if (data.strategy === 'quarterly') {
      return data.year && data.quarter;
    } else if (data.strategy === 'yearly') {
      return data.year;
    }
    return false;
  },
  {
    message: "Invalid parameters for the selected strategy",
  }
);

export type CreateChangelogRequest = z.infer<typeof CreateChangelogRequestSchema>;

// Import result DTOs
export const OpenApiImportResultSchema = z.object({
  success: z.boolean(),
  topicId: z.string().optional(),
  title: z.string(),
  version: z.string().optional(),
  endpointCount: z.number(),
  error: z.string().optional(),
});

export type OpenApiImportResult = z.infer<typeof OpenApiImportResultSchema>;

export const ChangelogGenerationResultSchema = z.object({
  success: z.boolean(),
  topicId: z.string(),
  title: z.string(),
  commitCount: z.number(),
  canonicalSlug: z.string().optional(),
  docId: z.string().optional(),
  error: z.string().optional(),
});

export type ChangelogGenerationResult = z.infer<typeof ChangelogGenerationResultSchema>;